import React from "react";
import "./feedback.css";

/**
 * Pop-ups et écrans de confirmation (design « Pop-ups et confirmations »).
 * `WoshModal` sert aux confirmations, `ResultScreen` aux pages de succès
 * et d'échec qui closent les parcours d'adresse.
 */

/** Les bulles qui montent derrière l'écran de succès : [gauche %, taille, délai, durée]. */
const BUBBLES = [
  [8, 18, 0, 9],
  [14, 10, 2.4, 7],
  [22, 26, 1, 11],
  [30, 12, 4, 8],
  [38, 8, 0.8, 6.5],
  [46, 20, 3, 10],
  [54, 14, 5.2, 8.5],
  [60, 30, 1.6, 12],
  [68, 10, 3.6, 7],
  [74, 22, 0.4, 10],
  [82, 12, 2.8, 8],
  [90, 16, 4.6, 9],
  [26, 8, 6, 6],
  [50, 10, 7, 7],
  [86, 8, 6.4, 6.5],
  [4, 12, 5, 8],
];

export const Bubbles = () => (
  <div className="wosh-done__bubbles" aria-hidden="true">
    {BUBBLES.map(([left, size, delay, duration], index) => (
      <span
        key={index}
        className="wosh-done__bubble"
        style={{
          left: `${left}%`,
          width: size,
          height: size,
          animationDelay: `${delay}s`,
          animationDuration: `${duration}s`,
        }}
      />
    ))}
  </div>
);

/**
 * Modale centrée sur voile flouté. On en sort par le bouton secondaire, par
 * Échap, par un clic sur le voile, et par la croix quand `closable` l'ajoute.
 *
 * @param {boolean}  open
 * @param {function} onClose
 * @param {node}     icon      Icône de la pastille d'en-tête.
 * @param {string}   title
 * @param {string}   lede
 * @param {node}     info      Encart d'information (adresse du point…).
 * @param {string}   error     Message d'erreur affiché au-dessus des actions.
 * @param {node}     warning   Encart rouge d'avertissement (conséquence
 *                             irréversible, comme la suppression du panier).
 * @param {boolean}  closable  Ajoute la croix en haut à droite.
 * @param {string}   note      Petite ligne sous les boutons.
 */
export const WoshModal = ({
  open,
  onClose,
  icon,
  title,
  lede,
  info,
  error,
  warning,
  closable = false,
  closeLabel = "Close",
  note,
  children,
  cancelLabel,
  confirmLabel,
  confirmDisabled = false,
  onConfirm,
}) => {
  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="wosh-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="wosh-modal__card">
        {closable && (
          <button
            type="button"
            className="wosh-modal__close"
            aria-label={closeLabel}
            onClick={onClose}
          >
            <svg viewBox="0 0 512 512" aria-hidden="true">
              <line x1="368" y1="368" x2="144" y2="144" />
              <line x1="368" y1="144" x2="144" y2="368" />
            </svg>
          </button>
        )}

        {icon && <div className="wosh-modal__badge">{icon}</div>}

        <h2 className="wosh-modal__title">{title}</h2>
        {lede && <p className="wosh-modal__lede">{lede}</p>}

        {info && <div className="wosh-modal__info">{info}</div>}

        {warning && (
          <div className="wosh-modal__warning" role="alert">
            <svg viewBox="0 0 512 512" aria-hidden="true">
              <path d="M248,64C146.39,64,64,146.39,64,248s82.39,184,184,184,184-82.39,184-184S349.61,64,248,64Z" />
              <polyline points="220 220 252 220 252 336" />
              <line x1="208" y1="340" x2="296" y2="340" />
              <circle cx="248" cy="156" r="22" className="wosh-modal__dot" />
            </svg>
            <span>{warning}</span>
          </div>
        )}

        {children}

        {error && <p className="wosh-modal__error">{error}</p>}

        <div className="wosh-modal__actions">
          <button type="button" className="wosh-btn wosh-btn--secondary" onClick={onClose}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="wosh-btn"
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>

        {note && <div className="wosh-modal__note">{note}</div>}
      </div>
    </div>
  );
};

/**
 * Page de fin de parcours. `tone` vaut "success" (halos, illustration) ou
 * "failure" (pastille rouge).
 *
 * Les animations suivent chaque écran du design, pas une règle commune :
 *   02 succès d'adresse     halo double, bulles, illustration qui flotte
 *   06 portefeuille rechargé halo moyen, rien ne bouge
 *   07 commande confirmée    halo large, bulles, illustration fixe
 * `prefers-reduced-motion` coupe tout (voir feedback.css).
 *
 * @param {string}  halo     "double" (défaut du succès) | "wide" | "medium"
 * @param {boolean} bubbles  Bulles qui montent (défaut : oui en succès).
 * @param {boolean} float    Illustration qui flotte (défaut : oui).
 * @param {string}  art      "large" (600 px, défaut) | "medium" (480) | "small" (380)
 * @param {Array}   facts    [{ label, value, accent }] : l'encart sous le texte.
 * @param {Array}   actions  [{ label, onClick, primary }]
 * @param {boolean} wideAction  Un seul bouton, plus large (320 px).
 */
export const ResultScreen = ({
  tone = "success",
  halo = "double",
  bubbles = true,
  float = true,
  art = "large",
  badge,
  title,
  lede,
  reason,
  illustration,
  fallbackIllustration,
  facts,
  factsSize = "regular",
  actions = [],
  wideAction = false,
}) => (
  <div className="wosh-done">
    {tone === "success" ? (
      <>
        {halo === "double" ? (
          <>
            <div className="wosh-done__halo wosh-done__halo--outer" />
            <div className="wosh-done__halo wosh-done__halo--inner" />
          </>
        ) : (
          <div className={`wosh-done__halo wosh-done__halo--${halo}`} />
        )}
        {bubbles && <Bubbles />}
      </>
    ) : (
      <div className="wosh-done__halo wosh-done__halo--single" />
    )}

    <div className="wosh-done__bar">
      <div className="wosh-done__wordmark">WOSH</div>
    </div>

    <div className="wosh-done__main">
      {tone === "success" ? (
        illustration && (
          <img
            className={`wosh-done__art wosh-done__art--${art}${float ? "" : " wosh-done__art--still"}`}
            src={illustration}
            alt=""
            onError={
              fallbackIllustration
                ? (event) => {
                    if (!event.currentTarget.src.endsWith(fallbackIllustration)) {
                      event.currentTarget.src = fallbackIllustration;
                    }
                  }
                : undefined
            }
          />
        )
      ) : (
        <div className="wosh-done__alert" aria-hidden="true">
          <span>!</span>
        </div>
      )}

      {badge && <div className="wosh-done__pill">{badge}</div>}

      <h1 className="wosh-done__title">{title}</h1>
      <p className={`wosh-done__lede${facts?.length ? " wosh-done__lede--tight" : ""}`}>{lede}</p>

      {reason && <div className="wosh-done__reason">{reason}</div>}

      {facts?.length > 0 && (
        <div className={`wosh-done__facts wosh-done__facts--${factsSize}`}>
          {facts.map((fact, index) => (
            <React.Fragment key={fact.label}>
              {index > 0 && <span className="wosh-done__facts-rule" aria-hidden="true" />}
              <div className="wosh-done__fact">
                <span className="wosh-done__fact-label">{fact.label}</span>
                <span className={`wosh-done__fact-value${fact.accent ? " wosh-done__fact-value--accent" : ""}`}>
                  {fact.value}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      <div
        className={`wosh-done__actions${
          actions.length < 2 ? " wosh-done__actions--single" : ""
        }${wideAction ? " wosh-done__actions--wide" : ""}`}
      >
        {actions.map((action) => (
          <button
            type="button"
            key={action.label}
            className={`wosh-btn${action.primary ? "" : " wosh-btn--secondary"}`}
            onClick={action.onClick}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

export default WoshModal;
